import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import { NavigateButton } from './button'
import StyledDatePicker from './date-picker'
import SelectBox from './select-box'
import { performanceDates, granularityFilterData } from '../core/variables'
import { getItemByKeyValue } from '../helpers'
import { setGranularityByDateRange } from '../helpers/report-module'
import useLogAction from '../hooks/useLogAction'
import styles from '../styles/performance-report-data-summary.module.scss'
import { GetCampaignLinkDashboardMetaQuery } from '../__gql-types__/graphql'

const yesterday = new Date(Date.now())
yesterday.setDate(yesterday.getDate() - 1)

interface PerformanceReportDataSummaryProps {
  currentDate: string
  setCurrentDate: React.Dispatch<React.SetStateAction<string>>
  startDate: Date
  setStartDate: React.Dispatch<React.SetStateAction<Date>>
  endDate: Date
  setEndDate: React.Dispatch<React.SetStateAction<Date>>
  matchType: MatchTypeProps
  setMatchType: React.Dispatch<React.SetStateAction<MatchTypeProps>>
  matchTypeMerged: MatchTypeProps[]
  availableDimensions: GetCampaignLinkDashboardMetaQuery['campaignLinkDashboardMeta']['availableDimensions']
  setDefaultSet: React.Dispatch<React.SetStateAction<boolean>>
  setBreakdownDimension: React.Dispatch<React.SetStateAction<string>>
  granularity: Granularity
  setGranularity: React.Dispatch<React.SetStateAction<Granularity>>
  successMetric: string
  setSuccessMetric: React.Dispatch<React.SetStateAction<string>>
  allAvailableMetrics: GetCampaignLinkDashboardMetaQuery['campaignLinkDashboardMeta']['availableMetrics']
  setShowTopLinksTable: React.Dispatch<React.SetStateAction<boolean>>
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}

export default function PerformanceReportDataSummary({
  currentDate,
  setCurrentDate,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  matchType,
  setMatchType,
  matchTypeMerged,
  availableDimensions,
  setDefaultSet,
  setBreakdownDimension,
  granularity,
  setGranularity,
  successMetric,
  setSuccessMetric,
  allAvailableMetrics,
  setShowTopLinksTable,
  setShowModal,
}: PerformanceReportDataSummaryProps) {
  const logAction = useLogAction()

  const [showCalendar, setShowCalendar] = useState(false)
  const [calendarStartsOpen, setCalendarStartsOpen] = useState(false)
  const [customDates, setCustomDates] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])

  useEffect(() => {
    if (startDate !== customDates[0] && endDate !== customDates[1]) {
      setCustomDates([startDate, endDate])
    }
  }, [startDate, endDate])

  // Placeholder to show instead of 'custom date range'
  const customDateCopy = useMemo(() => {
    const [start, end] = customDates

    if (!start || !end) return 'custom date range'

    const st = moment(start).format('DD/MM/YYYY')
    const en = moment(end).format('DD/MM/YYYY')

    return `${st} - ${en}`
  }, [customDates])

  // Remove custom dates when not custom
  useEffect(() => {
    if (currentDate !== 'custom') setCustomDates([null, null])

    setShowCalendar(currentDate === 'custom')
  }, [currentDate])

  // Set custom dates for report to update
  useEffect(() => {
    const [start, end] = customDates

    if (!start || !end) return

    setStartDate(start)
    setEndDate(end)
    setCurrentDate('custom')

    setGranularity(setGranularityByDateRange(start, end))

    const st = moment(start).format('YYYY-MM-DD')
    const en = moment(end).format('YYYY-MM-DD')
    const days = moment(end).diff(start, 'days')

    if (st !== en) {
      logAction({
        variables: {
          action: 'date-range-updated',
          extra: `custom - start: ${st} end: ${en} days: ${days}`,
          websiteSection: 'report',
          functionName: 'updateDateRange',
          pagePath: '/report/performance',
        },
      })
    }
  }, [customDates])

  return (
    <div className={styles.headerIntro}>
      <div className={styles.introCopyWrapper}>
        <div className={styles.introCopyBox}>
          <span className={styles.introCopy}>Compare </span>
          <SelectBox
            key={`${matchType.value}${!!availableDimensions}`}
            variant="grey"
            id="matchType"
            className={styles.matchTypeSelector}
            isSearchable={false}
            labelKey="name"
            value={matchType}
            options={matchTypeMerged}
            onChange={(newValue) => {
              if (!newValue) return

              const { value } = newValue

              setDefaultSet(true)
              const res = getItemByKeyValue(matchTypeMerged, 'value', value)

              if (res !== -1) {
                if (availableDimensions && availableDimensions.length > 0) {
                  setBreakdownDimension('')
                }
                setMatchType(res)

                logAction({
                  variables: {
                    action: 'matchtype-updated',
                    extra: value,
                    websiteSection: 'report',
                    functionName: 'updateMatchType',
                    pagePath: '/report/performance',
                  },
                })
              }
            }}
          />
        </div>
        <div className={styles.introCopyBox}>
          <span className={styles.introCopy}> over </span>
          <SelectBox
            key={currentDate}
            variant="grey"
            id="dateRange"
            className={styles.successMetricSelector}
            isSearchable={false}
            defaultValue={performanceDates.find(
              (date) => date.metricValue === 'thisYear',
            )}
            placeholder={customDateCopy}
            labelKey="displayName"
            valueKey="metricValue"
            value={
              showCalendar
                ? null
                : performanceDates.find(
                    (date) => date.metricValue === currentDate,
                  )
            }
            options={performanceDates}
            onChange={(newValue) => {
              if (!newValue) return

              const { metricValue } = newValue

              if (metricValue === 'custom') {
                // Do not update the date range until it has been selected
                setShowCalendar(true)
                setCalendarStartsOpen(true)
              } else {
                if (metricValue !== 'thisYear') {
                  setGranularity('daily')
                }

                setShowCalendar(false)
                setCurrentDate(metricValue)

                logAction({
                  variables: {
                    action: 'date-range-updated',
                    extra: metricValue,
                    websiteSection: 'report',
                    functionName: 'updateDateRange',
                    pagePath: '/report/performance',
                  },
                })
              }
            }}
          />
          {showCalendar && (
            <StyledDatePicker
              buttonOnly
              wrapperClassName={styles.dateWrapper}
              startOpen={calendarStartsOpen}
              selectsRange
              startDate={customDates[0]}
              endDate={customDates[1]}
              onChange={(dates) => setCustomDates(dates)}
              maxDate={new Date(Date.now())}
            />
          )}
        </div>
        <div className={styles.introCopyBox}>
          <span className={styles.introCopy}> ranked by </span>
          <SelectBox
            key={successMetric}
            variant="grey"
            id="successMetric"
            className={styles.successMetricSelector}
            placeholder="success metric"
            isLoading={!allAvailableMetrics || allAvailableMetrics.length === 0}
            labelKey="displayName"
            valueKey="metricID"
            value={
              allAvailableMetrics?.find(
                (metric) => metric.metricID === successMetric,
              ) || null
            }
            options={allAvailableMetrics || []}
            onChange={(newValue) => {
              if (!newValue) return

              const { metricID } = newValue

              setSuccessMetric(metricID)
              setShowTopLinksTable(true)

              logAction({
                variables: {
                  action: 'selected-metric-updated',
                  extra: metricID,
                  websiteSection: 'report',
                  functionName: 'updateSuccessMetric',
                  pagePath: '/report/performance',
                },
              })
            }}
          >
            <NavigateButton
              className={styles.editMetrics}
              onPressStart={() => setShowModal(true)}
            >
              Edit metrics
            </NavigateButton>
          </SelectBox>
        </div>
        <div className={styles.introCopyBox}>
          <span className={styles.introCopy}> grouped by </span>
          <SelectBox
            key={granularity}
            variant="grey"
            id="granularity"
            className={styles.granularitySelector}
            labelKey="name"
            isSearchable={false}
            defaultValue={granularityFilterData.find(
              (item) => item.value === 'monthly',
            )}
            value={granularityFilterData.find(
              (option) => option.value === granularity,
            )}
            options={granularityFilterData}
            onChange={(newValue) => {
              if (!newValue) return

              const { value } = newValue

              if (
                value === 'quarterly' ||
                value === 'monthly' ||
                value === 'weekly' ||
                value === 'daily'
              ) {
                setGranularity(value)

                logAction({
                  variables: {
                    action: 'granularity-updated',
                    extra: value,
                    websiteSection: 'report',
                    functionName: 'updateGranularity',
                    pagePath: '/report/performance',
                  },
                })
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
